import { Badge } from "react-bootstrap";
import {
	ACCESS_RIGHT_AREAS,
	ACCESS_RIGHT_TYPES,
} from "../../../constant/permissions";
import { createActionColumn } from "../../../utils/grid-columns";
import { getFormattedDate } from "../../../utils/method";
import capmoLogo from "../../../assets/images/capmo_logo.svg";

export const USER_TYPE_FILTERS = [
	{ value: "all", labelKey: "admin.users.type-all" },
	{ value: "global", labelKey: "admin.users.type-global" },
	{ value: "project-local", labelKey: "admin.users.type-project-local" },
];

export const getUserTypeChipFilter = (activeType) => ({
	type: "chipGroup",
	key: "userType",
	labelKey: "admin.users.filter-type",
	value: activeType,
	options: USER_TYPE_FILTERS.map(({ value, labelKey }) => ({
		value,
		labelKey,
	})),
});

const getGlobalAdminExtraColumns = (t, onProjectsClick) => [
	{
		field: "isProjectLocal",
		headerName: t("type"),
		headerTooltip: t("type"),
		maxWidth: 120,
		sortable: false,
		cellRenderer: (params) => (
			<Badge bg={params.value ? "info" : "secondary"}>
				{params.value
					? t("admin.users.type-badge-local")
					: t("admin.users.type-badge-global")}
			</Badge>
		),
	},
	{
		field: "projectCount",
		headerName: t("admin.users.projects-count"),
		headerTooltip: t("admin.users.projects-count"),
		maxWidth: 120,
		type: "numericColumn",
		cellRenderer: (params) => {
			const count = params.value ?? 0;
			if (count === 0) {
				return <span className="text-muted">0</span>;
			}
			return (
				<button
					type="button"
					className="btn btn-link btn-sm p-0 settings-users__project-count"
					onClick={(e) => {
						e.stopPropagation();
						onProjectsClick(params.data);
					}}
				>
					{count}
				</button>
			);
		},
	},
	{
		field: "isActive",
		headerName: t("status.status"),
		headerTooltip: t("status.status"),
		maxWidth: 110,
		cellRenderer: (params) => (
			<Badge bg={params.value !== false ? "success" : "secondary"}>
				{params.value !== false
					? t("admin.users.status-active")
					: t("admin.users.status-inactive")}
			</Badge>
		),
	},
];

export const getUserTableColumns = (
	t,
	onEdit,
	onDelete,
	currentUserId,
	{ isGlobalAdmin = false, onProjectsClick, onToggleStatus } = {},
) => {
	const baseColumns = [
		{
			field: "createdAt",
			headerName: t("users.provided"),
			headerTooltip: t("users.provided"),
			unSortIcon: true,
			valueFormatter: (params) => getFormattedDate(params.value),
			maxWidth: 160,
			minWidth: 160,
		},
		{
			field: "isFromCapmo",
			headerName: t("common.source"),
			headerTooltip: t("common.source"),
			unSortIcon: true,
			sortable: false,
			cellRenderer: (params) =>
				params.data.isFromCapmo && (
					<img
						src={capmoLogo}
						alt="CAPMO"
						height="16"
						width="16"
						title={t("lv.from-capmo")}
						className="d-inline-block"
					/>
				),
			maxWidth: 60,
		},
		{
			field: "firstName",
			headerName: t("users.first-name"),
			headerTooltip: t("users.first-name"),
			unSortIcon: true,
		},
		{
			field: "lastName",
			headerName: t("users.last-name"),
			headerTooltip: t("users.last-name"),
			unSortIcon: true,
		},
		{
			field: "email",
			headerName: t("users.email"),
			headerTooltip: t("users.email"),
			unSortIcon: true,
			minWidth: isGlobalAdmin ? 200 : undefined,
		},
		{
			field: "role.name",
			headerName: t("users.role"),
			headerTooltip: t("users.role"),
			unSortIcon: true,
			valueGetter: (params) => params.data.role?.name || "",
			valueFormatter: (params) => {
				const roleName = params.value;
				return roleName
					? t(`roles.types.${roleName}`, { defaultValue: roleName })
					: "";
			},
			maxWidth: 150,
		},
	];

	const companyColumn = {
		field: "company.name",
		headerName: t("users.company"),
		headerTooltip: t("users.company"),
		unSortIcon: true,
		valueGetter: (params) => params.data.company?.name || "",
	};

	const actionColumn = createActionColumn({
		t,
		actions: [
			{
				label: "help.action-edit",
				permission: {
					area: ACCESS_RIGHT_AREAS.USERS,
					action: ACCESS_RIGHT_TYPES.EDIT,
				},
				onClick: onEdit,
			},
			...(onToggleStatus
				? [
						{
							label: (data) =>
								data.isActive !== false
									? "users.deactivate"
									: "users.activate",
							permission: {
								area: ACCESS_RIGHT_AREAS.USERS,
								action: ACCESS_RIGHT_TYPES.EDIT,
							},
							onClick: onToggleStatus,
							show: (data) => data.id !== currentUserId,
						},
					]
				: []),
			{
				label: "help.action-delete",
				permission: {
					area: ACCESS_RIGHT_AREAS.USERS,
					action: ACCESS_RIGHT_TYPES.DELETE,
				},
				onClick: onDelete,
				danger: true,
				show: (data) => data.id !== currentUserId,
			},
		],
	});

	if (isGlobalAdmin && onProjectsClick) {
		return [
			...baseColumns,
			...getGlobalAdminExtraColumns(t, onProjectsClick),
			companyColumn,
			actionColumn,
		];
	}

	return [...baseColumns, companyColumn, actionColumn];
};
